import { useTranslation } from 'react-i18next'
import { recurrenceSummary } from '../format'
import type { AgendaDetail } from '../types'
import { DAY_KEYS, MONTH_KEYS, timeOf, weekdayIndex } from '../week'
import ResponseBadge from './ResponseBadge'

/** Bölgesiz duvar saatinden yerel takvim günü — dilim dönüşümü olmasın diye parçalardan kurulur. */
function dayOf(value: string): Date {
  const [y, m, d] = value.slice(0, 10).split('-').map(Number)
  return new Date(y, m - 1, d)
}

/** Toplantı özeti: takvim yaprağı + tarih/saat, düzenleyen, katılımcı sayısı ve detay metni. */
export default function AgendaSummaryCard({ agenda }: Readonly<{ agenda: AgendaDetail }>) {
  const { t } = useTranslation()
  const start = dayOf(agenda.startTime)
  const end = dayOf(agenda.endTime)
  const month = (d: Date) => t(`agenda.week.months.${MONTH_KEYS[d.getMonth()]}`)
  const weekday = (d: Date) => t(`agenda.week.days.${DAY_KEYS[weekdayIndex(d)]}`)
  const longDate = (d: Date) => `${d.getDate()} ${month(d)} ${d.getFullYear()}, ${weekday(d)}`
  const sameDay = start.getTime() === end.getTime()
  const when = sameDay
    ? `${longDate(start)} · ${timeOf(agenda.startTime)}–${timeOf(agenda.endTime)}`
    : `${longDate(start)} ${timeOf(agenda.startTime)} – ${longDate(end)} ${timeOf(agenda.endTime)}`

  return (
    <section className="agenda-summary" aria-label={t('agenda.detail.when')}>
      <div className="agenda-summary-date" aria-hidden="true">
        <span className="agenda-summary-month">{month(start)}</span>
        <span className="agenda-summary-day">{start.getDate()}</span>
        <span className="agenda-summary-weekday">{weekday(start)}</span>
      </div>
      <div className="agenda-summary-body">
        <ul className="agenda-summary-meta">
          <li>
            <i className="pi pi-clock" aria-hidden="true" />
            <span className="ui-sr-only">{t('agenda.detail.when')}: </span>
            {when}
          </li>
          {agenda.series && (
            <li>
              <i className="pi pi-sync" aria-hidden="true" />
              <span className="agenda-muted">{t('agenda.recurrence.label')}:</span>{' '}
              {recurrenceSummary(t, agenda.series)}
            </li>
          )}
          <li>
            <i className="pi pi-user" aria-hidden="true" />
            <span className="agenda-muted">{t('agenda.detail.organizer')}:</span> {agenda.ownerFullName ?? ''}
          </li>
          <li>
            <i className="pi pi-users" aria-hidden="true" />
            {t('agenda.attendeeCount', { count: agenda.participants.length })}
            {agenda.myParticipation && <ResponseBadge response={agenda.myParticipation.response} />}
          </li>
        </ul>
        {agenda.description && (
          <p className="agenda-summary-description agenda-description">{agenda.description}</p>
        )}
      </div>
    </section>
  )
}
